import { useEffect, useState } from "react";
import { CalendarCheck, Loader2, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { calendarEvents, calendarSync, calendarAuto, calendarRemove } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";

export const GoogleCalendarTab = ({ accounts, onChange, onReconnect }) => {
  const [ev, setEv] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => calendarEvents().then(setEv).catch(() => setEv({ events: [], accounts: [] }));
  useEffect(() => { load(); }, []);
  const ready = accounts.filter((a) => a.scopes?.calendar);
  const stale = accounts.filter((a) => !a.scopes?.calendar);
  const acct = ready[0];
  const toggle = async (v) => {
    try { const r = await calendarAuto(acct.id, v); toast.success(v ? `Auto-add on${r.added ? ` — ${r.added} reminder${r.added === 1 ? "" : "s"} added` : ""}` : "Auto-add off"); onChange(); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change"); }
  };
  const syncNow = async () => {
    setBusy(true);
    try { const r = await calendarSync(acct.id); toast.success(r.added ? `Added ${r.added} reminder${r.added === 1 ? "" : "s"} to Google Calendar` : "Everything upcoming is already on your calendar"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add"); } finally { setBusy(false); }
  };
  const remove = async (k) => { try { await calendarRemove(k); load(); } catch { toast.error("Couldn't remove"); } };
  return (
    <div className="space-y-3" data-testid="google-calendar-tab">
      <p className="text-xs text-muted-foreground">Renewals, expirations and check-ups from your documents become all-day events on your Google Calendar, with a reminder a week and a day before and a link straight back to the document.</p>
      {acct && (
        <div className="rounded-xl border border-border p-4 space-y-3" data-testid="calendar-account">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary grid place-items-center"><CalendarCheck className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1"><div className="font-medium truncate">{acct.email}</div><div className="text-xs text-muted-foreground">{ev ? `${ev.events.length} reminder${ev.events.length === 1 ? "" : "s"} on the calendar` : "…"}</div></div>
            <Button size="sm" variant="outline" className="rounded-full" disabled={busy} data-testid="calendar-sync-btn" onClick={syncNow}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add all upcoming"}</Button>
          </div>
          <label className="flex items-center justify-between gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs">Automatically add new renewals as documents are filed<Switch data-testid="calendar-auto-switch" checked={acct.calendar_auto} onCheckedChange={toggle} /></label>
          {ev && ev.events.length > 0 && (
            <ul className="divide-y divide-border text-xs max-h-48 overflow-auto">{ev.events.slice().sort((a, b) => a.date.localeCompare(b.date)).map((e) => (
              <li key={e.key} className="py-1.5 flex items-center gap-2" data-testid={`calendar-event-${e.key}`}>
                <span className="font-mono text-muted-foreground shrink-0">{e.date}</span><span className="flex-1 truncate">{e.label} — {e.document_title}</span>
                {e.html_link && <a href={e.html_link} target="_blank" rel="noreferrer" className="text-primary" title="Open in Google Calendar"><ExternalLink className="h-3 w-3" /></a>}
                <button className="text-muted-foreground hover:text-destructive" data-testid={`calendar-remove-${e.key}`} onClick={() => remove(e.key)}><X className="h-3.5 w-3.5" /></button>
              </li>))}</ul>
          )}
        </div>
      )}
      {stale.map((a) => (
        <div key={a.id} className="rounded-xl border border-dashed border-border p-3 text-xs flex items-center gap-2" data-testid={`calendar-reconnect-${a.id}`}>
          <span className="flex-1"><b>{a.email}</b> was connected before Calendar access was added.</span>
          <Button size="sm" variant="outline" className="rounded-full h-7" data-testid={`calendar-reconnect-btn-${a.id}`} onClick={onReconnect}>Reconnect to allow Calendar</Button>
        </div>
      ))}
    </div>
  );
};
